import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TOOL_MATERIALS, TOOL_TYPES } from '@bridgewerk/shared';
import type { ToolLibraryRecord } from '@bridgewerk/shared';
import { Button } from '../../../components/ui/Button.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useUpdateToolLibraryEntry } from '../hooks/useToolLibrary.js';

const schema = z.object({
  description:           z.string().min(1, 'Description is required').max(120),
  type:                  z.enum(TOOL_TYPES),
  material:              z.enum(TOOL_MATERIALS),
  manufacturer:          z.string().max(80).optional().or(z.literal('')),
  part_number:           z.string().max(80).optional().or(z.literal('')),
  diameter_mm:           z.coerce.number().positive().optional().or(z.literal('')),
  expected_life_cuts:    z.coerce.number().int().positive().optional().or(z.literal('')),
  expected_life_minutes: z.coerce.number().positive().optional().or(z.literal('')),
  notes:                 z.string().max(500).optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

const FIELD_CLASS =
  'w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-text placeholder:text-text-dim focus:border-accent focus:outline-none';
const LABEL_CLASS = 'block text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim mb-1';

export interface EditToolModalProps {
  readonly shopId: string;
  readonly tool: ToolLibraryRecord;
  readonly onClose: () => void;
}

export function EditToolModal({ shopId, tool, onClose }: EditToolModalProps): JSX.Element {
  const toast = useToast();
  const update = useUpdateToolLibraryEntry(shopId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      description:           tool.description,
      type:                  tool.type,
      material:              tool.material,
      manufacturer:          tool.manufacturer ?? '',
      part_number:           tool.part_number ?? '',
      diameter_mm:           tool.diameter_mm ?? ('' as unknown as number),
      expected_life_cuts:    tool.expected_life_cuts ?? ('' as unknown as number),
      expected_life_minutes: tool.expected_life_minutes ?? ('' as unknown as number),
      notes:                 tool.notes ?? '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({
        toolId: tool.tool_id,
        input: {
          description:           values.description,
          type:                  values.type,
          material:              values.material,
          manufacturer:          emptyToNull(values.manufacturer),
          part_number:           emptyToNull(values.part_number),
          diameter_mm:           numericOrNull(values.diameter_mm),
          expected_life_cuts:    numericOrNull(values.expected_life_cuts),
          expected_life_minutes: numericOrNull(values.expected_life_minutes),
          notes:                 emptyToNull(values.notes),
        },
      });
      toast.push({ tone: 'success', title: 'Tool updated', description: values.description });
      onClose();
    } catch (err) {
      toast.push({
        tone: 'critical',
        title: 'Could not update tool',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-tool-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-xl rounded-md border border-border bg-surface shadow-xl flex flex-col max-h-[90vh]">
        <header className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <h2 id="edit-tool-title" className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
              Edit tool
            </h2>
            <p className="mt-1 text-sm font-mono truncate">{tool.description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-dim hover:text-text leading-none px-2"
          >
            ✕
          </button>
        </header>

        <form onSubmit={onSubmit} noValidate className="overflow-y-auto flex-1 px-4 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Description *" error={errors.description?.message}>
              <input type="text" className={FIELD_CLASS} {...register('description')} />
            </Field>

            <Field label="Type *" error={errors.type?.message}>
              <select className={FIELD_CLASS} {...register('type')}>
                {(TOOL_TYPES as readonly string[]).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>

            <Field label="Material *" error={errors.material?.message}>
              <select className={FIELD_CLASS} {...register('material')}>
                {(TOOL_MATERIALS as readonly string[]).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>

            <Field label="Diameter (mm)" error={errors.diameter_mm?.message}>
              <input type="number" step="0.001" className={FIELD_CLASS} {...register('diameter_mm')} />
            </Field>

            <Field label="Manufacturer" error={errors.manufacturer?.message}>
              <input type="text" className={FIELD_CLASS} {...register('manufacturer')} />
            </Field>

            <Field label="Part number" error={errors.part_number?.message}>
              <input type="text" className={FIELD_CLASS} {...register('part_number')} />
            </Field>

            <Field label="Expected life (cuts)" error={errors.expected_life_cuts?.message}>
              <input type="number" min="1" className={FIELD_CLASS} {...register('expected_life_cuts')} />
            </Field>

            <Field label="Expected life (minutes)" error={errors.expected_life_minutes?.message}>
              <input type="number" min="1" className={FIELD_CLASS} {...register('expected_life_minutes')} />
            </Field>
          </div>

          <Field label="Notes" error={errors.notes?.message}>
            <textarea rows={2} className={FIELD_CLASS} {...register('notes')} />
          </Field>
        </form>

        <footer className="px-4 py-3 border-t border-border flex items-center justify-end gap-2 shrink-0">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={() => void onSubmit()} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  readonly label: string;
  readonly error?: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className={LABEL_CLASS}>{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red font-mono">{error}</span>}
    </label>
  );
}

function emptyToNull(v: string | undefined): string | null {
  return v === undefined || v === '' ? null : v;
}

function numericOrNull(v: number | string | undefined): number | null {
  if (v === undefined || v === '' || v === null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
